/** @format */
import { useTranslation } from 'next-i18next';
import React, { useContext } from 'react';

import { AttributeKey } from '@toucan/types/product';

import { formatAsCurrency, formatAsDecimal } from '@toucan/helpers/currency';
import {
    getAttributeValue,
    isConditionSparesAndRepairs,
    preparePriceModifiers,
    productConditionEnumToText,
} from '@toucan/helpers/products';

import OpenInNewTabIcon from '@toucan/icons/open-in-new-tab.svg';

import { MarketContext } from '@toucan/client/global/market';
import { WrappedLink } from '@toucan/client/global/nextjs';
import { ProductSummaryV2 } from '@toucan/client/global/product';
import { LinkTarget } from '@toucan/client/shared/components/link';
import { useProductUrl } from '@toucan/client/shared/hooks/use-product-url';

import { ListItem } from './components/in-stock-header/types';

export function useRowData(
    productData: ProductSummaryV2 | undefined,
    rowCount: number,
    modelUrlSegment: string
) {
    const { t } = useTranslation();
    const { activeMarket } = useContext(MarketContext);
    const { href, templatePath } = useProductUrl(
        productData?.sku,
        modelUrlSegment
    );

    if (!productData) {
        return [...Array(rowCount)].map((_, index) => ({
            id: index,
            label: null,
            value: null,
        }));
    }

    const {
        condition,
        attributes,
        priceModifiers,
        value,
        customDescription,
        sku,
        observations,
    } = productData;

    const formatPrice = formatAsCurrency(
        activeMarket.iso,
        activeMarket.currency
    );

    const listPrice = formatPrice(value / 100, { trailingZeros: false });
    const conditionText = productConditionEnumToText(t, condition);

    const { included: whatsIncluded, notIncluded: whatsNotIncluded } =
        preparePriceModifiers({
            priceModifiers,
            condition,
            activeMarket,
            t,
        });

    const shutterCount = getAttributeValue(
        attributes,
        AttributeKey.SHUTTER_COUNT
    );
    const hourCount = getAttributeValue(attributes, AttributeKey.HOUR_COUNT);
    const batteryChargeCount = getAttributeValue(
        attributes,
        AttributeKey.BATTERY_CHARGE_COUNT
    );
    const firmware = getAttributeValue(attributes, AttributeKey.FIRMWARE);
    const { label: usageCountLabel, value: usageCountValue } =
        getUsageCount() ?? {};

    function formatValue(value: string | ListItem[]) {
        if (!value?.length) {
            return '-';
        }

        if (typeof value === 'string') {
            return value;
        }

        return (
            <ul>
                {value.map((attr) => (
                    <li key={attr.id}>{attr.value}</li>
                ))}
            </ul>
        );
    }

    function getNotes() {
        if (isConditionSparesAndRepairs(condition)) {
            return customDescription;
        }

        return observations
            .filter(({ tierDescription }) => tierDescription)
            .map((observation) => ({
                id: `${observation.observationId}`,
                value: observation.tierDescription,
            }));
    }

    function getUsageCount() {
        if (hourCount !== null) {
            return {
                label: t('Hour count:'),
                value: hourCount
                    ? formatAsDecimal(activeMarket.iso, Number(hourCount))
                    : formatValue(hourCount),
            };
        }

        if (batteryChargeCount !== null) {
            return {
                label: t('Battery charge count:'),
                value: batteryChargeCount
                    ? formatAsDecimal(
                          activeMarket.iso,
                          Number(batteryChargeCount)
                      )
                    : formatValue(batteryChargeCount),
            };
        }

        if (shutterCount !== null) {
            return {
                label: t('Shutter count:'),
                value: shutterCount
                    ? formatAsDecimal(activeMarket.iso, Number(shutterCount))
                    : formatValue(shutterCount),
            };
        }

        return null;
    }

    const rows = [
        {
            id: 'price',
            label: t('Price:'),
            value: listPrice,
        },
        {
            id: 'condition',
            label: t('Cosmetic condition:'),
            value: conditionText,
        },
        {
            id: 'included',
            label: t("What's included:"),
            value: formatValue(
                whatsIncluded.map((modifier) => ({
                    id: `${modifier.id}`,
                    value: modifier.name,
                }))
            ),
        },
        {
            id: 'not-included',
            label: t("What's not included:"),
            value: formatValue(
                whatsNotIncluded.map((modifier) => ({
                    id: `${modifier.id}`,
                    value: modifier.name,
                }))
            ),
        },
        {
            id: 'notes',
            label: t('Notes:'),
            value: formatValue(getNotes()),
        },
        {
            id: 'view-more',
            label: t('Additional photos:'),
            value: (
                <WrappedLink
                    href={templatePath}
                    as={href}
                    target={LinkTarget.BLANK}
                    suffix={<OpenInNewTabIcon />}
                >
                    {t('View full details')}
                </WrappedLink>
            ),
        },
        {
            id: 'sku',
            label: t('SKU:'),
            value: (
                <WrappedLink
                    href={templatePath}
                    as={href}
                    suffix={<OpenInNewTabIcon />}
                    target={LinkTarget.BLANK}
                >
                    {sku}
                </WrappedLink>
            ),
        },
    ];

    if (firmware !== null) {
        rows.splice(2, 0, {
            id: 'firmware',
            label: t('Firmware version:'),
            value: formatValue(firmware),
        });
    }

    if (usageCountValue && usageCountLabel) {
        rows.splice(2, 0, {
            id: 'usage-count',
            label: usageCountLabel,
            value: usageCountValue,
        });
    }

    return rows;
}
